import { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/common/ui-bits";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";

const when = new Intl.DateTimeFormat("en-US", { timeZone: "America/Nassau", dateStyle: "medium", timeStyle: "short" });

const STATUS = {
  sent: "bg-emerald-50 text-emerald-800 border-emerald-200",
  failed: "bg-rose-100 text-rose-800 border-rose-300",
  partial: "bg-amber-100 text-amber-900 border-amber-300",
  pending: "bg-sky-50 text-sky-800 border-sky-200",
  retrying: "bg-sky-50 text-sky-800 border-sky-200",
  skipped: "bg-secondary text-muted-foreground border-border",
  no_recipients: "bg-secondary text-muted-foreground border-border",
};
const STATUS_LABEL = { no_recipients: "no recipients", pending: "sending" };
const IN_FLIGHT = ["pending", "retrying"];

export function AlertLog({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const load = useCallback(() => api.get("/budgets/alerts-log").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const busy = rows?.some((r) => IN_FLIGHT.includes(r.status));
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [busy, load]);

  const retry = async (id) => {
    setRetrying(id);
    try {
      await api.post(`/budgets/alerts-log/${id}/retry`);
      toast.success("Retrying alert email");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    setRetrying(null);
  };

  return (
    <section className="surface overflow-hidden mt-8 fade-up" data-testid="alert-log">
      <div className="px-4 sm:px-5 pt-4 pb-3">
        <h2 className="font-heading text-lg font-semibold">Alert emails</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Budget alerts are emailed once per category, threshold and month; due-date reminders once per bill per day. They go to every active admin who has "Email me budget alerts" on.</p>
      </div>
      {rows?.length === 0 && <EmptyState icon={Mail} text="No alert emails yet" testId="alert-log-empty" />}
      {rows?.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-foreground text-white text-[11px] uppercase tracking-wider">
              <tr>{["Date", "Category", "Threshold", "Spent / budget", "Recipients", "Status"].map((h) => <th key={h} className="px-4 py-2.5 text-left font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border align-top" data-testid={`alert-log-row-${r.id}`}>
                  <td className="px-4 py-2.5 whitespace-nowrap">{when.format(new Date(r.created_at))}</td>
                  <td className="px-4 py-2.5" data-testid={`alert-log-category-${r.id}`}>
                    {r.category_name.replace(" | ", " · ")}
                    {r.scope === "bill_reminder" && <div className="text-[11px] text-muted-foreground">Due-date reminder</div>}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap" data-testid={`alert-log-threshold-${r.id}`}>{r.scope === "bill_reminder" ? r.label : `${r.threshold}% · ${r.label}`}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap font-num">{r.scope === "bill_reminder" ? `Balance ${money(r.budget_cents - r.actual_cents)}` : `${money(r.actual_cents)} / ${money(r.budget_cents)}`}</td>
                  <td className="px-4 py-2.5 text-xs" data-testid={`alert-log-recipients-${r.id}`}>
                    {r.recipients.length ? r.recipients.join(", ") : "—"}
                    {r.results?.some((x) => x.delivered_to && x.delivered_to !== x.email) && (
                      <div className="text-[11px] text-amber-800 mt-1" data-testid={`alert-log-redirect-${r.id}`}>Test mode: delivered to {r.results[0].delivered_to}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS[r.status]}`} data-testid={`alert-log-status-${r.id}`}>
                      {IN_FLIGHT.includes(r.status) && <Loader2 className="h-3 w-3 animate-spin" />}
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                    {r.status !== "sent" && r.error && <div className="text-[11px] text-muted-foreground mt-1 max-w-[240px]" data-testid={`alert-log-error-${r.id}`}>{r.error}</div>}
                    {["failed", "partial"].includes(r.status) && (
                      <Button size="sm" variant="outline" className="mt-2 h-7 rounded-lg text-xs" disabled={retrying === r.id}
                        onClick={() => retry(r.id)} data-testid={`alert-log-retry-${r.id}`}>
                        <RotateCw className={`h-3 w-3 mr-1 ${retrying === r.id ? "animate-spin" : ""}`} />Retry
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
